/**
 * ItemSystem — Singleton for item creation, management, and querying.
 * All item data is JSON-driven from items.json and affixes.json.
 *
 * @module ItemSystem
 */

import DebugLog from '../debug/DebugLog.js';
import ITEMS_DATA from '../data/items.json';
import AFFIXES_DATA from '../data/affixes.json';

// ─── Helpers ──────────────────────────────────────────────────────────────────

let _instanceCounter = 0;

function generateInstanceId() {
  _instanceCounter += 1;
  return `item_${Date.now().toString(36)}_${_instanceCounter.toString(36)}`;
}

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickRandom(arr) {
  if (!arr || arr.length === 0) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}

// ─── Rarity config ────────────────────────────────────────────────────────────

const RARITY_AFFIX_COUNTS = {
  common:    { prefixes: 0, suffixes: 0 },
  magic:     { prefixes: 1, suffixes: 0 },   // 1 prefix OR 1 suffix
  rare:      { prefixes: 1, suffixes: 1 },
  legendary: { prefixes: 1, suffixes: 1 },   // + implicit handled separately
  unique:    { prefixes: 0, suffixes: 0 },
};

const RARITY_COLORS = {
  common:    '#a0a0a0',
  magic:     '#4a90e0',
  rare:      '#e0c030',
  legendary: '#e07820',
  unique:    '#c83030',
};

// Starting gear by background
const BACKGROUND_STARTING_GEAR = {
  soldier:        ['iron_sword', 'iron_shield', 'leather_armor'],
  mercenary:      ['iron_axe', 'leather_armor', 'health_potion_small'],
  scholar:        ['wooden_staff', 'cloth_robe', 'mana_potion_small'],
  healer:         ['apprentice_wand', 'cloth_robe', 'health_potion_small', 'health_potion_small', 'health_potion_small'],
  skald:          ['iron_sword', 'leather_armor', 'ration_good'],
  thief:          ['iron_dagger', 'leather_armor', 'lockpick_set'],
  ranger:         ['short_bow', 'quiver', 'leather_armor'],
  sea_captain:    ['iron_sword', 'leather_armor', 'rope_and_hook'],
  runecarver:     ['wooden_staff', 'cloth_robe', 'scroll_of_identify'],
  caravan_guard:  ['iron_spear', 'leather_armor', 'repair_kit'],
  farmer:         ['iron_hammer', 'leather_armor', 'ration_poor'],
  merchant_child: ['iron_dagger', 'leather_armor', 'health_potion_small'],
  noble_heir:     ['iron_sword', 'leather_armor', 'copper_ring'],
  street_urchin:  ['iron_dagger', 'leather_armor', 'lockpick_set'],
  apprentice_mage:['apprentice_wand', 'cloth_robe', 'scroll_of_identify'],
  fisher_child:   ['iron_spear', 'leather_armor', 'ration_poor'],
  blacksmith_child:['iron_hammer', 'leather_armor', 'repair_kit', 'repair_kit'],
  forest_child:   ['short_bow', 'quiver', 'leather_armor'],
};

const DEFAULT_STARTING_GEAR = ['iron_sword', 'leather_armor', 'health_potion_small'];

// ─── ItemSystem ───────────────────────────────────────────────────────────────

const ItemSystem = {
  /** @type {Map<string, object>} Base item definitions keyed by id */
  _items: new Map(),

  /** @type {Array} Prefix affix definitions */
  _prefixes: [],

  /** @type {Array} Suffix affix definitions */
  _suffixes: [],

  /** @type {boolean} */
  _initialized: false,

  /**
   * Initialize ItemSystem — loads JSON data into in-memory maps.
   * Safe to call multiple times.
   */
  init() {
    if (this._initialized) return;

    // Load base items
    for (const item of ITEMS_DATA) {
      this._items.set(item.id, Object.freeze({ ...item }));
    }

    // Load affixes
    this._prefixes = AFFIXES_DATA.prefixes ?? [];
    this._suffixes = AFFIXES_DATA.suffixes ?? [];

    this._initialized = true;
    DebugLog.info(`[item] ItemSystem initialized; items=${this._items.size} prefixes=${this._prefixes.length} suffixes=${this._suffixes.length}`);
  },

  // ─── Base item queries ────────────────────────────────────────────────────

  /**
   * Get a base item definition by ID.
   * @param {string} id
   * @returns {object|null}
   */
  getBaseItem(id) {
    const item = this._items.get(id);
    if (!item) {
      DebugLog.warn(`[item] getBaseItem: unknown id "${id}"`);
      return null;
    }
    return item;
  },

  /**
   * Get all base items of a given slot.
   * @param {string} slot
   * @returns {object[]}
   */
  getItemsBySlot(slot) {
    return [...this._items.values()].filter(i => i.slot === slot);
  },

  /**
   * Get all base items of a given type.
   * @param {string} type
   * @returns {object[]}
   */
  getItemsByType(type) {
    return [...this._items.values()].filter(i => i.type === type);
  },

  // ─── Instance creation ────────────────────────────────────────────────────

  /**
   * Create a runtime item instance from a base item ID.
   * @param {string} baseId
   * @param {object} [options]
   * @param {string} [options.rarity] — Override rarity
   * @param {number} [options.level] — Override level
   * @param {boolean} [options.identified] — Force identified state
   * @param {string[]} [options.affixIds] — Specific affixes to apply
   * @returns {object|null} Item instance
   */
  createInstance(baseId, options = {}) {
    const base = this.getBaseItem(baseId);
    if (!base) return null;

    const rarity = options.rarity ?? base.rarity ?? 'common';
    const level  = options.level  ?? base.level  ?? 1;

    // Determine if identified based on rarity
    let identified = options.identified ?? true;
    if (options.identified === undefined) {
      const unidentChance = { common: 0, magic: 0.20, rare: 0.50, legendary: 0.80, unique: 1.0 };
      identified = Math.random() > (unidentChance[rarity] ?? 0);
    }

    // Roll affixes
    let affixes = [];
    if (options.affixIds) {
      affixes = options.affixIds.map(aid => {
        const def = [...this._prefixes, ...this._suffixes].find(a => a.id === aid);
        return def ? { id: def.id, type: def.type, identified } : null;
      }).filter(Boolean);
    } else if (rarity !== 'common') {
      affixes = this.rollAffixes(base, rarity).map(a => ({ ...a, identified }));
    }

    const instance = {
      instanceId:    generateInstanceId(),
      baseId,
      name:          this._buildName(base, affixes, identified),
      rarity,
      identified,
      level,
      affixes,
      quantity:      base.stackable ? 1 : 1,
      durability:    base.durability ?? 100,
      maxDurability: base.maxDurability ?? 100,
    };

    DebugLog.info(`[item] Created instance; id=${instance.instanceId} base=${baseId} rarity=${rarity}`);
    return instance;
  },

  /**
   * Generate a random item appropriate for a given level, slot, and rarity.
   * @param {number} level
   * @param {string|null} slot — Specific slot or null for random
   * @param {string} [rarity='common']
   * @returns {object|null} Item instance
   */
  generateItem(level, slot, rarity = 'common') {
    let pool = [...this._items.values()].filter(i => i.level <= level);
    if (slot) {
      pool = pool.filter(i => i.slot === slot);
    }
    // Exclude tools and consumables from random generation by default
    pool = pool.filter(i => i.type !== 'tool');

    if (pool.length === 0) {
      DebugLog.warn(`[item] generateItem: no items found for level=${level} slot=${slot}`);
      return null;
    }

    const base = pickRandom(pool);
    return this.createInstance(base.id, { level, rarity });
  },

  /**
   * Roll affixes for a given base item and rarity.
   * Returns array of { id, type } objects.
   * @param {object} baseItem
   * @param {string} rarity
   * @returns {Array<{id: string, type: string}>}
   */
  rollAffixes(baseItem, rarity) {
    const config = RARITY_AFFIX_COUNTS[rarity] ?? RARITY_AFFIX_COUNTS.common;
    const result = [];
    const slot = baseItem.slot ?? baseItem.type ?? 'weapon';

    const eligiblePrefixes = this._prefixes.filter(p =>
      p.minLevel <= (baseItem.level ?? 1) &&
      (p.applicableSlots.includes(slot) || p.applicableSlots.includes(baseItem.type ?? ''))
    );

    const eligibleSuffixes = this._suffixes.filter(s =>
      s.minLevel <= (baseItem.level ?? 1) &&
      (s.applicableSlots.includes(slot) || s.applicableSlots.includes(baseItem.type ?? ''))
    );

    // Magic tier: either 1 prefix OR 1 suffix (50/50)
    if (rarity === 'magic') {
      if (Math.random() < 0.5 && eligiblePrefixes.length > 0) {
        const p = pickRandom(eligiblePrefixes);
        result.push({ id: p.id, type: 'prefix' });
      } else if (eligibleSuffixes.length > 0) {
        const s = pickRandom(eligibleSuffixes);
        result.push({ id: s.id, type: 'suffix' });
      } else if (eligiblePrefixes.length > 0) {
        const p = pickRandom(eligiblePrefixes);
        result.push({ id: p.id, type: 'prefix' });
      }
      return result;
    }

    // Rare / Legendary: 1 prefix + 1 suffix
    if (config.prefixes > 0 && eligiblePrefixes.length > 0) {
      const p = pickRandom(eligiblePrefixes);
      result.push({ id: p.id, type: 'prefix' });
    }
    if (config.suffixes > 0 && eligibleSuffixes.length > 0) {
      const s = pickRandom(eligibleSuffixes);
      result.push({ id: s.id, type: 'suffix' });
    }

    return result;
  },

  /**
   * Reveal all affixes on an item instance.
   * @param {object} instance — Item instance (mutated in place)
   * @returns {object} The updated instance
   */
  identifyItem(instance) {
    instance.identified = true;
    if (instance.affixes) {
      instance.affixes.forEach(a => { a.identified = true; });
    }
    // Rebuild display name
    const base = this.getBaseItem(instance.baseId);
    if (base) {
      instance.name = this._buildName(base, instance.affixes, true);
    }
    DebugLog.info(`[item] Identified; id=${instance.instanceId} name=${instance.name}`);
    return instance;
  },

  /**
   * Build the display name for an item from its base name and affixes.
   * @param {object} base
   * @param {Array} affixes
   * @param {boolean} identified
   * @returns {string}
   */
  getItemName(instance) {
    const base = this.getBaseItem(instance.baseId);
    if (!base) return instance.name ?? 'Unknown Item';
    return this._buildName(base, instance.affixes, instance.identified);
  },

  /**
   * @private
   */
  _buildName(base, affixes, identified) {
    if (!identified) return `Unidentified ${base.name}`;

    const prefix = affixes?.find(a => a.type === 'prefix' && a.identified);
    const suffix = affixes?.find(a => a.type === 'suffix' && a.identified);

    const prefixDef = prefix ? this._prefixes.find(p => p.id === prefix.id) : null;
    const suffixDef = suffix ? this._suffixes.find(s => s.id === suffix.id) : null;

    let name = base.name;
    if (prefixDef) name = `${prefixDef.name} ${name}`;
    if (suffixDef) name = `${name} ${suffixDef.name}`;

    return name;
  },

  /**
   * Check whether a character can equip a given item instance.
   * @param {object} character — Character data object
   * @param {object} instance — Item instance
   * @returns {{ canEquip: boolean, warnings: string[] }}
   */
  canEquip(character, instance) {
    const base = this.getBaseItem(instance.baseId);
    if (!base) return { canEquip: false, warnings: ['Unknown item.'] };

    const warnings = [];

    // Attribute requirements
    const reqs = base.requirements ?? {};
    for (const [attr, required] of Object.entries(reqs)) {
      const charVal = character.attributes?.[attr] ?? 0;
      if (charVal < required) {
        warnings.push(`Requires ${attr} ${required} (have ${charVal})`);
      }
    }

    // No-slot items (consumables/tools) can't be equipped
    if (!base.slot) {
      return { canEquip: false, warnings: ['This item cannot be equipped.'] };
    }

    // Requirements met or not — item CAN be equipped but with penalties if requirements fail
    return { canEquip: true, warnings };
  },

  /**
   * Get the effective total weight of an item instance.
   * Accounts for lightweight affix.
   * @param {object} instance
   * @returns {number}
   */
  getItemWeight(instance) {
    const base = this.getBaseItem(instance.baseId);
    if (!base) return 0;

    let weight = base.weight ?? 0;

    // Apply lightweight affix
    const stats = this.getEffectiveStats(instance);
    if (stats.weightMultiplier !== undefined) {
      weight = weight * stats.weightMultiplier;
    }

    return Math.max(0, weight);
  },

  /**
   * Get the combined effective stats of an item instance (base + all identified affixes).
   * @param {object} instance
   * @returns {object} Merged stats object
   */
  getEffectiveStats(instance) {
    const base = this.getBaseItem(instance.baseId);
    if (!base) return {};

    // Start with base stats
    const stats = {
      damage:        base.damage         ?? null,
      defense:       base.defense        ?? 0,
      blockChance:   base.blockChance    ?? 0,
      weight:        base.weight         ?? 0,
      manaBonus:     base.manaBonus      ?? 0,
      mightBonus:    base.mightBonus     ?? 0,
      armorPen:      base.armorPen       ?? 0,
      critBonus:     base.critBonus      ?? 0,
      stunChance:    base.stunChance     ?? 0,
      reach:         base.reach          ?? 0,
      moveSpeedBonus:base.moveSpeedBonus ?? 0,
      carryBonus:    base.carryBonus     ?? 0,
      spellPowerBonus:base.spellPowerBonus ?? 0,
      lightRadius:   base.lightRadius    ?? 0,
    };

    // Merge affix effects
    const affixes = instance.affixes ?? [];
    for (const affix of affixes) {
      if (!affix.identified) continue;
      const allAffixes = [...this._prefixes, ...this._suffixes];
      const def = allAffixes.find(a => a.id === affix.id);
      if (!def) continue;

      const effect = def.effect ?? {};

      if (effect.critChanceBonus)      stats.critBonus          += effect.critChanceBonus;
      if (effect.dodgeChanceBonus)     stats.dodgeBonus          = (stats.dodgeBonus ?? 0) + effect.dodgeChanceBonus;
      if (effect.maxHPBonus)           stats.maxHPBonus          = (stats.maxHPBonus ?? 0) + effect.maxHPBonus;
      if (effect.maxManaBonus)         stats.manaBonus          += effect.maxManaBonus;
      if (effect.allResistanceBonus)   stats.allResistBonus      = (stats.allResistBonus ?? 0) + effect.allResistanceBonus;
      if (effect.goldFindBonus)        stats.goldFindBonus       = (stats.goldFindBonus ?? 0) + effect.goldFindBonus;
      if (effect.xpBonus)              stats.xpBonus             = (stats.xpBonus ?? 0) + effect.xpBonus;
      if (effect.moveSpeedBonus)       stats.moveSpeedBonus     += effect.moveSpeedBonus;
      if (effect.holyDamageBonus)      stats.holyDamageBonus     = (stats.holyDamageBonus ?? 0) + effect.holyDamageBonus;
      if (effect.weightMultiplier)     stats.weightMultiplier    = (stats.weightMultiplier ?? 1) * effect.weightMultiplier;
      if (effect.physicalDamageBonus)  stats.physicalDamageBonus = (stats.physicalDamageBonus ?? 0) + effect.physicalDamageBonus;
      if (effect.baseDamagePenalty)    stats.baseDamagePenalty   = (stats.baseDamagePenalty ?? 0) + effect.baseDamagePenalty;
      if (effect.stunChance)           stats.stunChance         += effect.stunChance;

      if (effect.bonusDamage) {
        stats.bonusDamage = stats.bonusDamage ?? [];
        stats.bonusDamage.push(effect.bonusDamage);
      }
      if (effect.onHitStatus) {
        stats.onHitStatuses = stats.onHitStatuses ?? [];
        stats.onHitStatuses.push(effect.onHitStatus);
      }
      if (effect.skillLevelBonus) {
        stats.skillLevelBonuses = stats.skillLevelBonuses ?? [];
        stats.skillLevelBonuses.push(effect.skillLevelBonus);
      }
      if (effect.attributeBonus) {
        stats.attributeBonuses = stats.attributeBonuses ?? {};
        for (const [attr, val] of Object.entries(effect.attributeBonus)) {
          stats.attributeBonuses[attr] = (stats.attributeBonuses[attr] ?? 0) + val;
        }
      }
    }

    return stats;
  },

  /**
   * Create starting gear for a character based on their adult background.
   * Returns an array of item instances.
   * @param {object} background — { childhood, adult }
   * @returns {object[]} Array of item instances
   */
  createStartingGear(background) {
    const adultBg = background?.adult ?? 'soldier';
    const gearIds = BACKGROUND_STARTING_GEAR[adultBg] ?? DEFAULT_STARTING_GEAR;

    const instances = [];
    for (const id of gearIds) {
      const instance = this.createInstance(id, { rarity: 'common', identified: true });
      if (instance) instances.push(instance);
    }

    DebugLog.info(`[item] Starting gear created; background=${adultBg} count=${instances.length}`);
    return instances;
  },

  /**
   * Use a consumable item instance on a character.
   * Applies effects based on the base item's `effect` field.
   * Removes the item from the character's inventory (or decrements quantity) after use.
   *
   * @param {object} instance — Item instance
   * @param {object} character — Character data object (mutated in place)
   * @param {object} [options={}]
   * @param {object} [options.worldState] — WorldState reference for ration/gold effects
   * @param {object} [options.targetItem] — For scroll_of_identify: item instance to identify
   * @returns {{ applied: boolean, message: string }}
   */
  useConsumable(instance, character, options = {}) {
    const base = this.getBaseItem(instance.baseId);
    if (!base) return { applied: false, message: 'Unknown item.' };

    if (base.type !== 'consumable') {
      return { applied: false, message: 'Item is not a consumable.' };
    }

    const effect = base.effect ?? {};
    const messages = [];

    // ── Health restoration ────────────────────────────────────────────────
    if (effect.restoreHP != null) {
      const before = character.hp ?? 0;
      const maxHP  = character.maxHP ?? before;
      character.hp = Math.min(maxHP, before + effect.restoreHP);
      const healed = character.hp - before;
      messages.push(`Restored ${healed} HP`);
      DebugLog.event(`[item] useConsumable; type=restoreHP character=${character.name} healed=${healed}`);
    }

    // ── Mana restoration ─────────────────────────────────────────────────
    if (effect.restoreMana != null) {
      const before   = character.mana ?? 0;
      const maxMana  = character.maxMana ?? before;
      character.mana = Math.min(maxMana, before + effect.restoreMana);
      const restored = character.mana - before;
      messages.push(`Restored ${restored} Mana`);
      DebugLog.event(`[item] useConsumable; type=restoreMana character=${character.name} restored=${restored}`);
    }

    // ── Status removal ────────────────────────────────────────────────────
    if (effect.removeStatus && Array.isArray(effect.removeStatus)) {
      const statusArr = character.statusEffects ?? [];
      const removed = [];
      for (const statusId of effect.removeStatus) {
        const idx = statusArr.findIndex(s =>
          (typeof s === 'string' ? s : s.id) === statusId
        );
        if (idx !== -1) {
          removed.push(statusId);
          statusArr.splice(idx, 1);
        }
      }
      character.statusEffects = statusArr;
      if (removed.length > 0) {
        messages.push(`Removed: ${removed.join(', ')}`);
      } else {
        messages.push('No matching status effects to remove.');
      }
      DebugLog.event(`[item] useConsumable; type=removeStatus character=${character.name} removed=${removed.join(',')}`);
    }

    // ── Skill book ────────────────────────────────────────────────────────
    if (effect.skillPoint != null) {
      character.skillPoints = (character.skillPoints ?? 0) + effect.skillPoint;
      messages.push(`+${effect.skillPoint} skill point`);
      DebugLog.event(`[item] useConsumable; type=skillPoint character=${character.name}`);
    }

    // ── Attribute flask ───────────────────────────────────────────────────
    if (effect.attributeBonus) {
      for (const [attr, val] of Object.entries(effect.attributeBonus)) {
        if (character.attributes && attr in character.attributes) {
          character.attributes[attr] = (character.attributes[attr] ?? 0) + val;
          messages.push(`+${val} ${attr}`);
        }
      }
      DebugLog.event(`[item] useConsumable; type=attributeBonus character=${character.name}`);
    }

    // ── Scroll of Identify ────────────────────────────────────────────────
    if (effect.identify) {
      const target = options.targetItem;
      if (!target) {
        return { applied: false, message: 'Select an unidentified item first.' };
      }
      if (target.identified) {
        return { applied: false, message: `${target.name} is already identified.` };
      }
      this.identifyItem(target);
      messages.push(`Identified: ${target.name}`);
      DebugLog.event(`[item] useConsumable; type=identify item=${target.instanceId}`);
    }

    // ── Rations ───────────────────────────────────────────────────────────
    if (effect.rationPoints != null) {
      const ws = options.worldState;
      if (ws) {
        ws.rationPoints = (ws.rationPoints ?? 0) + effect.rationPoints;
        messages.push(`+${effect.rationPoints} ration points`);
      }
      DebugLog.event(`[item] useConsumable; type=rationPoints value=${effect.rationPoints}`);
    }

    // ── Durability repair ─────────────────────────────────────────────────
    if (effect.restoreDurability != null) {
      // Repair kit — applies to the item with lowest durability in inventory
      const allItems = [
        ...(character.inventory ?? []),
        ...Object.values(character.equipment ?? {}).filter(Boolean),
      ];
      const damaged = allItems
        .filter(i => i && i.durability != null && i.maxDurability != null)
        .sort((a, b) => (a.durability / a.maxDurability) - (b.durability / b.maxDurability));

      if (damaged.length > 0) {
        const repairTarget = damaged[0];
        const before = repairTarget.durability;
        repairTarget.durability = Math.min(
          repairTarget.maxDurability,
          before + effect.restoreDurability
        );
        const amount = repairTarget.durability - before;
        messages.push(`Repaired ${repairTarget.name} +${amount} durability`);
        DebugLog.event(`[item] useConsumable; type=restoreDurability target=${repairTarget.instanceId} amount=${amount}`);
      } else {
        messages.push('No items to repair.');
      }
    }

    // ── Consume the item ──────────────────────────────────────────────────
    const inventory = character.inventory ?? [];
    const idx = inventory.indexOf(instance);
    if (idx !== -1) {
      if (base.stackable && instance.quantity > 1) {
        instance.quantity -= 1;
      } else {
        inventory.splice(idx, 1);
      }
    }

    const message = messages.length > 0 ? messages.join('; ') : 'Used.';
    return { applied: true, message };
  },

  /**
   * Get rarity color hex string.
   * @param {string} rarity
   * @returns {string}
   */
  getRarityColor(rarity) {
    return RARITY_COLORS[rarity] ?? RARITY_COLORS.common;
  },

  /**
   * Get a human-readable description of an affix by ID.
   * @param {string} affixId
   * @returns {string}
   */
  getAffixDescription(affixId) {
    const def = [...this._prefixes, ...this._suffixes].find(a => a.id === affixId);
    return def?.description ?? '';
  },
};

// ─── Debug exposure ───────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__debugGetItem = (id) => ItemSystem.getBaseItem(id);
  window.__debugCreateItem = (baseId, opts) => ItemSystem.createInstance(baseId, opts);
}

export default ItemSystem;
